// Ik this code is a lot of repeat from code in other places, but I do have some things I plan on doing which would make me using the other code less desirable for this purpose

import { mat4, vec2, vec3 } from "gl-matrix";
import { WorldMap } from "../map/Map";
import { BVHTriangle, Mesh } from "../map/Mesh";
import { Camera } from "../render/Camera";
import { RenderUtils } from "../utils/RenderUtils";
import { TextureUtils } from "../utils/TextureUtils";
import { WorldUtils } from "../utils/WorldUtils";
import { DebugMenu } from "../DebugMenu";
import pathTracingFragmentShaderCode from "./glsl/pathtracerShader/path.frag";
import pathTracingVertexShaderCode from "./glsl/pathtracerShader/path.vert";
import { BVHUtils } from "../map/BVHUtils";
import copyFragmentShader from "./glsl/copyShader/copy.frag";
import copyVertexShader from "./glsl/copyShader/copy.vert";
import { NoiseGenerator } from "../render/passes/CloudsPass";
import { Terrains } from "../map/terrains";
import { SettingsManager } from "../Settings";
import { VAOManager } from "../render/renderSystem/managers/VaoManager";

export class PathTracer {
  //Rendering
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  // Accumulation stuff
  private accumulationFrameBuffers: WebGLFramebuffer[] = [];
  private varianceFrameBuffers: WebGLFramebuffer[] = [];
  private accumulationTextures: WebGLTexture[] = [];
  private varianceTextures: WebGLTexture[] = [];
  private currentFrame = 0; // The source texture/framebuffer index
  private frameNumber = 0; // The accumulation counter
  //Shaders
  public meshProgram: WebGLProgram | null = null;

  private fullscreenVAO: WebGLVertexArrayObject | null = null;
  private fullscreenVBO: WebGLBuffer | null = null;

  //Information
  private vertices: Float32Array = null!;
  private terrains: Float32Array = null!;
  private boundingBoxes: Float32Array = null!;
  // BVH
  private nodes: Float32Array = null!;
  private leafs: Float32Array = null!;
  // Terrain Info
  private terrainTypes: Float32Array = null!;
  private vertexNormals: Float32Array = null!;
  private grassBB: Float32Array = null!;

  //Classes
  private world: WorldMap;
  private camera: Camera;
  private debug: DebugMenu;
  private noiseGenerator: NoiseGenerator;
  public glRendererVaoManager: VAOManager | null = null;

  //textures
  private vertexTex?: WebGLTexture;
  private terrainTex?: WebGLTexture;
  private boundingBoxesTex?: WebGLTexture;
  private nodesTex?: WebGLTexture;
  private leafsTex?: WebGLTexture;
  private terrainTypeTex?: WebGLTexture;
  private vertexNormalsTex?: WebGLTexture;
  private grassTexture?: WebGLTexture;
  private noiseTexture?: WebGLTexture;
  private weatherMapTexture?: WebGLTexture;

  private totalTime = 0;
  private startTime = Date.now();

  private uniforms = {
    vertices: null as WebGLUniformLocation | null,
    terrains: null as WebGLUniformLocation | null,
    boundindingBoxes: null as WebGLUniformLocation | null,
    nodes: null as WebGLUniformLocation | null,
    leafs: null as WebGLUniformLocation | null,
    terrainTypes: null as WebGLUniformLocation | null,
    vertexNormal: null as WebGLUniformLocation | null,
    numTerrains: null as WebGLUniformLocation | null,
    camera: null as WebGLUniformLocation | null,
    inverseViewProj: null as WebGLUniformLocation | null,
    resolution: null as WebGLUniformLocation | null,
    lastFrame: null as WebGLUniformLocation | null,
    frameNum: null as WebGLUniformLocation | null,
    grassBB: null as WebGLUniformLocation | null,
    copyBoolean: null as WebGLUniformLocation | null,
    lastVariance: null as WebGLUniformLocation | null,
  };

  public constructor(
    canvas: HTMLCanvasElement,
    context: WebGL2RenderingContext,
    world: WorldMap,
    camera: Camera,
    debug: DebugMenu
  ) {
    this.canvas = canvas;
    this.gl = context;
    this.world = world;
    this.camera = camera;
    this.debug = debug;
    this.noiseGenerator=new NoiseGenerator(this.gl);
    //this.gl.enable(this.gl.BLEND);
    this.gl.disable(this.gl.BLEND); // Prevent MRT data corruption

    //Enable float texture writing extention
    const float_render_ext = this.gl.getExtension("EXT_color_buffer_float");
    if (!float_render_ext) {
      alert(
        "Error: Floating point render targets are not supported on this browser/GPU."
      );
      throw new Error("EXT_color_buffer_float not supported");
    }

    //Shaders
    console.time("Pathtracer Shader Compile Time");
    // Start compilation
    const programPromise = RenderUtils.CreateProgramAsync(
      this.gl,
      pathTracingVertexShaderCode,
      pathTracingFragmentShaderCode
    );

    // WAIT for the program before initializing uniforms and textures
    programPromise.then((program) => {
      if (program) {
        this.meshProgram = program;
        this.initSettingsSection();

        console.log("PathTracer: Shader compiled and system initialized.");
        console.timeEnd("Pathtracer Shader Compile Time");

      }
    }).catch((error) => {
      // Handle specific errors thrown during compilation
      console.error("PathTracer: Fatal error during shader compilation:", error);
    });;


    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        // User came back
        this.startTime = Date.now();
      }
    });
  }
  public initBVH(mainMesh: Mesh) {
    ////////////////////// build flat BVH structure
    //Obtain bvh from mesh.
    const BVHtriangles = mainMesh.exportBVHTriangles();
    if(this.glRendererVaoManager){
      const grassInfo = this.glRendererVaoManager.getGrassBVHTriangle();
      let grassTriangles = grassInfo.triangles;
      for(let i = 0; i < grassTriangles.length; i++){
        BVHtriangles.push(grassTriangles[i]);
      }
      if(grassTriangles.length != 0){
        this.grassBB = grassInfo.primitives;
      }
    }
    const BVHtree = Mesh.exportBVH(BVHtriangles);
    const flatBVHtree = Mesh.flattenBVH(BVHtree);

    ////////////// Pack everything float format to send to glsl
    //Pack triangles
    const { vertices, terrains, normals } = BVHUtils.packTriangles(
      mainMesh.mesh,
      mainMesh.type,
      mainMesh.normals
    );
    //Pack BVH
    const { boundingBoxes, nodes, leafs } = BVHUtils.packBVH(flatBVHtree);
    //Pack terrain Types
    const terrainTypes = BVHUtils.packTerrainTypes();
    //save
    this.vertices = vertices;
    this.terrains = terrains;
    this.boundingBoxes = boundingBoxes;
    this.nodes = nodes;
    this.leafs = leafs;
    this.terrainTypes = terrainTypes;
    this.vertexNormals = normals;
  }
  public render(time: number) {
    this.gl.clearColor(0.0, 0.0, 0.0, 1.0);
    // Clear the color buffer with specified clear color
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);

    //const resScaleFactor = 1 / (this.world.resolution / 4);
    this.drawMesh();
  }

  public drawMesh() {
    this.setupFrame();
    this.gl.finish(); // Ensure all setup commands are done before we start timing

    //Put camera position, direction in shader
    this.gl.uniform1i(this.uniforms.copyBoolean, 0);
    this.gl.uniform3fv(
      this.uniforms.camera,
      this.camera.position
    );
    const viewProjMatrix = this.camera.calculateProjectionMatrix(
      this.canvas.width,
      this.canvas.height
    );
    const invViewProjMatrix = mat4.create();
    mat4.invert(invViewProjMatrix, viewProjMatrix);
    this.gl.uniformMatrix4fv(
      this.uniforms.inverseViewProj,
      false,
      invViewProjMatrix
    );
    const resolution = vec2.create();
    resolution[0] = this.canvas.width;
    resolution[1] = this.canvas.height;
    this.gl.uniform2fv(
      this.uniforms.resolution,
      resolution
    );

    //put lights in the shader
    WorldUtils.updateLights(this.gl, this.meshProgram!, this.world.lights, this.world.sunLight);

    //Bind Previous Frame
    const lastFrameIndex = this.currentFrame;
    const nextFrameIndex = (this.currentFrame + 1) % 2;

    // previous accumulation
    this.gl.activeTexture(this.gl.TEXTURE10); // Changed to 10
    this.gl.bindTexture(
      this.gl.TEXTURE_2D,
      this.accumulationTextures[lastFrameIndex]
    );
    this.gl.uniform1i(this.uniforms.lastFrame, 10);

    // previous variance
    this.gl.activeTexture(this.gl.TEXTURE11); // Changed to 11
    this.gl.bindTexture(
      this.gl.TEXTURE_2D,
      this.varianceTextures[lastFrameIndex]
    );
    this.gl.uniform1i(this.uniforms.lastVariance, 11);

    //put samples, bounce in shader
    SettingsManager.instance.updateProgramUniforms(this.gl,this.meshProgram!);
    this.frameNumber++;
    this.gl.uniform1i(
      this.uniforms.frameNum,
      this.frameNumber
    ); // Send as a float for seeding

    // Draw
    this.gl.bindFramebuffer(
      this.gl.FRAMEBUFFER,
      this.accumulationFrameBuffers[nextFrameIndex]
    );

    this.gl.drawBuffers([
      this.gl.COLOR_ATTACHMENT0,
      this.gl.COLOR_ATTACHMENT1
    ]);
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
    //Ping Pong
    this.currentFrame = nextFrameIndex;

    //Draw to canvas using copy shader
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    /*this.gl.useProgram(this.copyProgram);

    TextureUtils.bindTex(
      this.gl,
      this.copyProgram,
      this.accumulationTextures[nextFrameIndex],
      this.uniforms.COPYPROGRAM_sourceTex!,
      0
    );*/
    this.gl.uniform1i(this.uniforms.copyBoolean, 1);
    this.gl.activeTexture(this.gl.TEXTURE10); // Match the new unit
    this.gl.bindTexture(
      this.gl.TEXTURE_2D,
      this.accumulationTextures[nextFrameIndex]
    );
    this.gl.uniform1i(this.uniforms.lastFrame, 10);
    // We can reuse the same fullscreen triangle VAO
    this.gl.clearColor(0, 0, 0, 1); // Clear the actual screen
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
    this.gl.bindVertexArray(null); 

    this.totalTime += Date.now() - this.startTime;
    this.startTime = Date.now();
  }

  public makeVao() {
    if (this.fullscreenVAO) return; // Already created once

    const fullscreenTriangle = new Float32Array([-1, -1, 3, -1, -1, 3]);
    const vao = this.gl.createVertexArray();
    this.gl.bindVertexArray(vao);

    const vbo = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, vbo);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, fullscreenTriangle, this.gl.STATIC_DRAW);

    this.gl.enableVertexAttribArray(0);
    this.gl.vertexAttribPointer(0, 2, this.gl.FLOAT, false, 0, 0);

    this.fullscreenVAO = vao;
    this.fullscreenVBO = vbo;

    // Unbind to avoid polluting other pipelines
    this.gl.bindVertexArray(null);
  }

  public init(showAccumulation: boolean = true) {
    if (showAccumulation){
      this.debug.addElement("Accumulation Frame", () => this.frameNumber);
      this.debug.addElement("Accumulation Time (s)", () => Math.round(this.totalTime/1000));
      this.debug.addElement("Resolution", () => this.canvas.width + "x" + this.canvas.height);
      this.camera.farPlane = this.camera.pathtracingFarPlane;
    }
    this.uniforms.vertices = this.gl.getUniformLocation(this.meshProgram!, "u_vertices");
    this.uniforms.terrains = this.gl.getUniformLocation(this.meshProgram!, "u_terrains");
    this.uniforms.boundindingBoxes = this.gl.getUniformLocation(this.meshProgram!, "u_boundingBox");
    this.uniforms.nodes = this.gl.getUniformLocation(this.meshProgram!, "u_nodesTex");
    this.uniforms.leafs = this.gl.getUniformLocation(this.meshProgram!, "u_leafsTex");
    this.uniforms.terrainTypes = this.gl.getUniformLocation(this.meshProgram!, "u_terrainTypes");
    this.uniforms.vertexNormal = this.gl.getUniformLocation(this.meshProgram!, "u_normals");
    this.uniforms.numTerrains = this.gl.getUniformLocation(this.meshProgram!, "u_numTerrains");
    this.uniforms.camera = this.gl.getUniformLocation(this.meshProgram!, "u_cameraPos");
    this.uniforms.inverseViewProj = this.gl.getUniformLocation(this.meshProgram!, "u_invViewProjMatrix");
    this.uniforms.resolution = this.gl.getUniformLocation(this.meshProgram!, "u_resolution");
    this.uniforms.lastFrame = this.gl.getUniformLocation(this.meshProgram!, "u_lastFrame");
    this.uniforms.frameNum = this.gl.getUniformLocation(this.meshProgram!, "u_frameNumber");
    this.uniforms.grassBB = this.gl.getUniformLocation(this.meshProgram!, "u_grassBB");
    this.uniforms.copyBoolean = this.gl.getUniformLocation(this.meshProgram!, "u_copyMode");
    this.uniforms.lastVariance = this.gl.getUniformLocation(this.meshProgram!, "u_varianceTexture");

    this.initBVHTextures();
    this.setupFrame();
    this.makeVao();
    this.resetAccumulation();
    this.bindBVH();
  }

  private bindBVH(){
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.vertexTex!, this.uniforms.vertices!, 0);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.terrainTex!, this.uniforms.terrains!, 1);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.boundingBoxesTex!, this.uniforms.boundindingBoxes!, 2);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.nodesTex!, this.uniforms.nodes!, 3);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.leafsTex!, this.uniforms.leafs!, 4);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.terrainTypeTex!, this.uniforms.terrainTypes!, 5);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.vertexNormalsTex!, this.uniforms.vertexNormal!, 6);
    TextureUtils.bindTex(this.gl, this.meshProgram!, this.grassTexture!, this.uniforms.grassBB!, 9);


    //NOTE: When we fix natively pathtraced clouds we will put this back.
    /*
    this.gl.activeTexture(this.gl.TEXTURE7);
    this.gl.bindTexture(this.gl.TEXTURE_3D, this.noiseTexture!);
    
    this.gl.uniform1i(
      this.gl.getUniformLocation(this.meshProgram, "u_CloudNoise"),
      7
    );
    //TextureUtils.bindTex(this.gl, this.meshProgram, this.weatherMapTexture!, "u_WeatherMap", 8);

    this.gl.uniform3fv(
      this.gl.getUniformLocation(this.meshProgram, "u_cloudsCubeMin"),
      vec3.fromValues(-300, 100, -300)
    );
    this.gl.uniform3fv(
      this.gl.getUniformLocation(this.meshProgram, "u_cloudsCubeMax"),
      vec3.fromValues(300, 160, 300)
    );*/

    this.gl.uniform1i(
      this.uniforms.numTerrains,
      Object.keys(Terrains).length
    );
  }

  public leave() {
    this.debug.removeElement("Accumulation Frame");
    this.debug.removeElement("Accumulation Time (s)");
    this.debug.removeElement("Resolution");
    this.camera.farPlane = this.camera.rayTracingFarPlane;
  }
  private initBVHTextures() {
    // --- MEMORY LEAK FIX: Delete old BVH buffers ---
    if (this.vertexTex) this.gl.deleteTexture(this.vertexTex);
    if (this.terrainTex) this.gl.deleteTexture(this.terrainTex);
    if (this.boundingBoxesTex) this.gl.deleteTexture(this.boundingBoxesTex);
    if (this.nodesTex) this.gl.deleteTexture(this.nodesTex);
    if (this.leafsTex) this.gl.deleteTexture(this.leafsTex);
    if (this.terrainTypeTex) this.gl.deleteTexture(this.terrainTypeTex);
    if (this.vertexNormalsTex) this.gl.deleteTexture(this.vertexNormalsTex);
    if (this.grassTexture) this.gl.deleteTexture(this.grassTexture);

    this.vertexTex = TextureUtils.packFloatArrayToTexture(this.gl, this.vertices);
    this.terrainTex = TextureUtils.packFloatArrayToTexture(this.gl, this.terrains);
    this.boundingBoxesTex = TextureUtils.packFloatArrayToTexture(this.gl, this.boundingBoxes);
    this.nodesTex = TextureUtils.packFloatArrayToTexture(this.gl, this.nodes);
    this.leafsTex = TextureUtils.packFloatArrayToTexture(this.gl, this.leafs);
    this.terrainTypeTex = TextureUtils.packFloatArrayToTexture(this.gl, this.terrainTypes);
    this.vertexNormalsTex = TextureUtils.packFloatArrayToTexture(this.gl, this.vertexNormals);
    this.grassTexture = TextureUtils.packFloatArrayToTexture(this.gl, this.grassBB);

    // --- TIME FIX: Only generate expensive 3D noise ONCE ---
    if (!this.noiseTexture) {
      this.noiseTexture = this.noiseGenerator.generateCloudNoiseTex(32);
    }
    if (!this.weatherMapTexture) {
      this.weatherMapTexture = this.noiseGenerator.generateWeatherMap(128);
    }
  }

  private setupFrame() {
    this.gl.useProgram(this.meshProgram);
    const ext = this.gl.getExtension("EXT_color_buffer_float");
    if (!ext) console.warn("No float render targets available.");
    
    //VAO
    this.gl.bindVertexArray(this.fullscreenVAO);
  }

  private initBuffers() {
    // --- MEMORY LEAK FIX: Delete old buffers before redefining them ---
    if (this.accumulationTextures) this.accumulationTextures.forEach(t => this.gl.deleteTexture(t));
    if (this.varianceTextures) this.varianceTextures.forEach(t => this.gl.deleteTexture(t));
    if (this.accumulationFrameBuffers) this.accumulationFrameBuffers.forEach(f => this.gl.deleteFramebuffer(f));

    this.accumulationTextures = [];
    this.varianceTextures =[];
    this.accumulationFrameBuffers =[];

    for (let i = 0; i < 2; i++) {
      // --- Accumulation texture (32-bit float) ---
      const accumTex = this.gl.createTexture()!;
      this.gl.bindTexture(this.gl.TEXTURE_2D, accumTex);
      this.gl.texImage2D(
        this.gl.TEXTURE_2D, 0, this.gl.RGBA32F,
        this.canvas.width, this.canvas.height, 0,
        this.gl.RGBA, this.gl.FLOAT, null
      );
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
      this.accumulationTextures.push(accumTex);

      // --- Variance texture (16-bit float) ---
      const varianceTex = this.gl.createTexture()!;
      this.gl.bindTexture(this.gl.TEXTURE_2D, varianceTex);
      this.gl.texImage2D(
        this.gl.TEXTURE_2D, 0, this.gl.RGBA16F,
        this.canvas.width, this.canvas.height, 0,
        this.gl.RGBA, this.gl.HALF_FLOAT, null // <-- STRICT FIX: Use HALF_FLOAT for 16F buffers
      );
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
      this.varianceTextures.push(varianceTex);

      // --- Framebuffer ---
      const fbo = this.gl.createFramebuffer()!;
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, fbo);
      this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, accumTex, 0);
      this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT1, this.gl.TEXTURE_2D, varianceTex, 0);
      
      this.gl.drawBuffers([
        this.gl.COLOR_ATTACHMENT0,
        this.gl.COLOR_ATTACHMENT1
      ]);

      this.gl.clearBufferfv(this.gl.COLOR, 0, [0, 0, 0, 0]);
      this.gl.clearBufferfv(this.gl.COLOR, 1,[0, 0, 0, 0]);

      const status = this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER);
      if (status !== this.gl.FRAMEBUFFER_COMPLETE) {
        console.error("PathTracer Framebuffer incomplete:", status);
      }
      this.accumulationFrameBuffers.push(fbo);
    }
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
  }

  public resetAccumulation() {
    this.frameNumber = 1;
    this.totalTime = 0;
    this.startTime = Date.now();
    this.initBuffers();
  }

  public dispose() {
    // delete accumulation textures/framebuffers
    if (this.accumulationTextures) {
      for (const tex of this.accumulationTextures) {
        if (tex) this.gl.deleteTexture(tex);
      }
      this.accumulationTextures = [];
    }
    if (this.varianceTextures) {
      for (const tex of this.varianceTextures) {
        if (tex) this.gl.deleteTexture(tex);
      }
      this.varianceTextures = [];
    }
    if (this.accumulationFrameBuffers) {
      for (const fbo of this.accumulationFrameBuffers) {
        if (fbo) this.gl.deleteFramebuffer(fbo);
      }
      this.accumulationFrameBuffers = [];
    }
    if (this.varianceFrameBuffers) {
      for (const fbo of this.varianceFrameBuffers) {
        if (fbo) this.gl.deleteFramebuffer(fbo);
      }
      this.varianceFrameBuffers = [];
    }
    // delete programs
    if (this.meshProgram) this.gl.deleteProgram(this.meshProgram);
    // delete fullscreen VAO/VBO
    if (this.fullscreenVAO) {
      this.gl.deleteVertexArray(this.fullscreenVAO);
      this.fullscreenVAO = null;
    }
    if (this.fullscreenVBO) {
      this.gl.deleteBuffer(this.fullscreenVBO);
      this.fullscreenVBO = null;
    }

    // Clean up massive geometry buffers to prevent them bleeding into the rasterizer
    if (this.vertexTex) this.gl.deleteTexture(this.vertexTex);
    if (this.terrainTex) this.gl.deleteTexture(this.terrainTex);
    if (this.boundingBoxesTex) this.gl.deleteTexture(this.boundingBoxesTex);
    if (this.nodesTex) this.gl.deleteTexture(this.nodesTex);
    if (this.leafsTex) this.gl.deleteTexture(this.leafsTex);
    if (this.terrainTypeTex) this.gl.deleteTexture(this.terrainTypeTex);
    if (this.vertexNormalsTex) this.gl.deleteTexture(this.vertexNormalsTex);
    if (this.grassTexture) this.gl.deleteTexture(this.grassTexture);
    
    // Optional: Delete noise textures if you don't plan on reusing them shortly
    if (this.noiseTexture) this.gl.deleteTexture(this.noiseTexture);
    if (this.weatherMapTexture) this.gl.deleteTexture(this.weatherMapTexture);

    this.vertexTex = undefined;
    this.terrainTex = undefined;
    this.boundingBoxesTex = undefined;
    this.nodesTex = undefined;
    this.leafsTex = undefined;
    this.terrainTypeTex = undefined;
    this.vertexNormalsTex = undefined;
    this.grassTexture = undefined;
    this.noiseTexture = undefined;
    this.weatherMapTexture = undefined;
  
  }

  private initSettingsSection() {
    SettingsManager.instance.createSection(
      document.getElementById("settings-section")!,
      "Pathtracer Settings"
    );
    SettingsManager.instance.addSliderToSection("Pathtracer Settings",{
      id: "numBounces",
      label: "Maximum Number of Bounces",
      min: 1,
      max: 20,
      step: 1,
      defaultValue: 5,
      numType: "int"
    });
    SettingsManager.instance.addSliderToSection("Pathtracer Settings",{
      id: "u_skips",
      label: "Skips",
      min: 1,
      max: 50,
      step: 1,
      defaultValue: 4,
      numType: "int"
    });
    SettingsManager.instance.addSliderToSection("Pathtracer Settings",{
      id: "u_varianceThreshold",
      label: "Variance Threshold",
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.05,
      numType: "float"
    });

    // Attach program uniforms for all settings
    SettingsManager.instance.attatchProgram(this.meshProgram!, [
      "numBounces",
      "u_skips",
      "u_varianceThreshold",
      "u_redScatter",
      "u_greenScatter",
      "u_blueScatter",
      "u_haloSize",
      "u_skyGradientQuality",
      "u_sunsetQuality",
      "u_MIE",
      "ambientLightIntensity",
      "u_skyBrightnessBoost",
      //NOTE: When we fix natively pathtraced clouds we will put this back.
      /*"CLOUDS_enableClouds",
      "CLOUDS_MAX_STEPS",
      "CLOUDS_MAX_STEPS_LIGHT",
      "CLOUDS_weatherMapOffsetX",
      "CLOUDS_weatherMapOffsetY",
      "CLOUDS_absorption",
      "CLOUDS_densityThreshold",
      "CLOUDS_baseFrequency",
      "CLOUDS_detailFrequency",
      "CLOUDS_lightAbsorption",
      "CLOUDS_lightIntensity",
      "CLOUDS_ambientIntensity",
      "CLOUDS_darknessThreshold",
      "CLOUDS_phaseG",
      "CLOUDS_phaseMultiplier",
      "CLOUDS_blueNoiseAmplitude",
      "CLOUDS_baseCloudColor",
      "CLOUDS_skyContribution",
      "CLOUDS_lightDarkSharpness",
      "CLOUDS_simplexMultiplier",*/
      //Grass
      "grassBaseColor",
      "grassEnabled",
      "grassTipColor"
    ]);
  }
}